import { api_matcha_calldata, fetchAndRetryIfNecessary } from '$lib/apis/matchaCallData';
import type { Token, TokenSymbol } from '$lib/types/tokens';
import { SUSD } from '$lib/web3/addresses';
import {
	amountPerTokenToSwap,
	loadedStablePricesSuccessfully,
	loadingMatchaCallData,
	matchaCallData,
	stablesPrices,
	swapSlippage
} from '$lib/home/store';
import type { IMatchaResponse, IStablesPrices } from '$lib/web3/types';
import BigNumber from 'bignumber.js';
import { get } from 'svelte/store';
import { getNumBackInUnits, sortInDescending } from '$lib/util/util';
import { stablesAllowedForDeposit } from '$lib/home/scwallet/config';
import { TokenBucketRateLimiter } from '$lib/types/matcha';
import { setStablesToPyth } from './pyth';

export async function getStablesPrices(
	userBalances: Record<TokenSymbol, Token> | Record<string, never>
) {
	let promiseArray: Promise<IStablesPrices>[] = [];
	const maxRequests = 1;
	const maxRequestWindowMS = 1100;
	const tokenBucket = new TokenBucketRateLimiter({ maxRequests, maxRequestWindowMS });

	if (userBalances) {
		stablesAllowedForDeposit.forEach((token: TokenSymbol) => {
			const tokenPromise = new Promise<IStablesPrices>(async (resolve, reject) => {
				if (userBalances[token] && userBalances[token].balance != '0') {
					tokenBucket.acquireToken(async () => {
						const calldata = await fetchAndRetryIfNecessary(
							userBalances[token].address,
							SUSD,
							new BigNumber(userBalances[token].balance),
							getNumBackInUnits(get(swapSlippage).toString(), 20).toString()
						);
						resolve({
							price: new BigNumber(calldata?.guaranteedPrice as string)
								.multipliedBy(1e8)
								.toFixed(0),
							symbol: token
						});
					});
				} else if (userBalances[token] && userBalances[token].balance == '0') {
					resolve({
						price: new BigNumber('1').multipliedBy(1e8).toFixed(0),
						symbol: token
					});
				}
			});
			promiseArray.push(tokenPromise);
		});
		const value = await Promise.all(promiseArray);
		stablesPrices.set(value);
		setStablesToPyth(value);
		loadedStablePricesSuccessfully.set(true);
		return;
	}

	let defaultStablesPrices: IStablesPrices[] = [];
	stablesAllowedForDeposit.forEach((token: TokenSymbol) => {
		defaultStablesPrices.push({
			price: new BigNumber('1').multipliedBy(1e8).toFixed(0),
			symbol: token
		});
	});
	stablesPrices.set(defaultStablesPrices);
	setStablesToPyth(defaultStablesPrices);
}

export async function findRequiredCallData(
	tokensAllowedForDepositBalance: Record<string, Token>,
	marginSizeDeltaDollars: BigNumber // 10^18
) {
	loadingMatchaCallData.set(true);
	let amountOfEachToken: Record<string, BigNumber> = {};
	let amountAccountedFor: BigNumber = new BigNumber(
		tokensAllowedForDepositBalance['sUSD'].balanceUSD
	);

	let tokensInDescendingOrderBalance: Token[];

	tokensInDescendingOrderBalance = sortInDescending(tokensAllowedForDepositBalance);

	for (let i = 0; i < tokensInDescendingOrderBalance.length; i++) {
		let currentTokenBalance = tokensInDescendingOrderBalance[i];
		if (currentTokenBalance.synth === 'sUSD') {
			continue;
		}
		if (amountAccountedFor.gte(marginSizeDeltaDollars)) break;

		if (new BigNumber(currentTokenBalance.balanceUSD).lte(0)) {
			continue;
		}

		const currentTokenBalanceAdjustedDecimals = new BigNumber(
			currentTokenBalance.balanceUSD
		).multipliedBy(`1e${18 - parseInt(currentTokenBalance.decimals)}`);

		if (
			amountAccountedFor
				.plus(currentTokenBalanceAdjustedDecimals)
				.isLessThanOrEqualTo(marginSizeDeltaDollars)
		) {
			amountOfEachToken[currentTokenBalance.synth] = new BigNumber(currentTokenBalance.balance);
			amountAccountedFor = amountAccountedFor.plus(
				new BigNumber(currentTokenBalance.balanceUSD).multipliedBy(
					`1e${18 - parseInt(currentTokenBalance.decimals)}`
				)
			);
		} else {
			amountOfEachToken[currentTokenBalance.synth] = marginSizeDeltaDollars
				.minus(amountAccountedFor)
				.multipliedBy(currentTokenBalance.balance)
				.div(currentTokenBalance.balanceUSD)
				.multipliedBy(`1e${currentTokenBalance.decimals}`)
				.dividedBy(1e18)
				.decimalPlaces(0);

			break;
		}
	}
	const { apiErrored, matchaResponse } = await fetchCallData(
		amountOfEachToken,
		tokensAllowedForDepositBalance
	);

	if (!apiErrored) {
		matchaCallData.set(matchaResponse);
		amountPerTokenToSwap.set(amountOfEachToken);
	}
	loadingMatchaCallData.set(false);
}

async function fetchCallData(
	amountOfEachToken: Record<string, BigNumber>,
	tokensAllowedForDepositBalance: Record<string, Token>
) {
	let apiErrored: boolean = false;
	let matchaResponse: Record<string, IMatchaResponse> = {};
	for (const tokenKey in amountOfEachToken) {
		if (tokenKey === 'sUSD') {
			continue;
		}

		try {
			let currentTokenAmount = amountOfEachToken[tokenKey];
			if (!currentTokenAmount) {
				break;
			}

			if (currentTokenAmount.gt(0)) {
				const data = await api_matcha_calldata(
					tokensAllowedForDepositBalance[tokenKey].address,
					SUSD,
					currentTokenAmount,
					getNumBackInUnits(get(swapSlippage).toString(), 20).toString()
				);
				if (!data) {
					apiErrored = true;
					break;
				}
				matchaResponse[tokenKey] = data;
			}
		} catch (err) {
			apiErrored = true;
			console.log(err);
			break;
		}
	}

	return {
		apiErrored,
		matchaResponse
	};
}
